import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Skeleton,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Help as HelpIcon
} from '@mui/icons-material';

const MetricCard = ({
  title,
  value,
  change,
  trend = 'stable',
  icon,
  variant = 'default',
  loading = false,
  subtitle,
  format = 'number',
  description
}) => {
  const theme = useTheme();

  const getVariantColor = () => {
    switch (variant) {
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />;
      default:
        return <TrendingFlatIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />;
    }
  };

  const formatValue = (val) => {
    if (loading) return "";

    // Handle undefined, null, or invalid values
    if (
      val === undefined ||
      val === null ||
      (typeof val === "object" && val.constructor === Error)
    ) {
      return "—"; // Em dash for missing data
    }

    switch (format) {
      case "percentage":
        return `${val}%`;
      case "currency":
        return `$${val.toLocaleString()}`;
      case "time":
        return `${val}min`;
      case "hours":
        return `${val}h`;
      default:
        return typeof val === "number" ? val.toLocaleString() : val || "—";
    }
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          borderLeft: 4,
          borderLeftColor: getVariantColor(),
          boxShadow: theme.shadows[2],
          '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between', 
            mb: 2,
            minHeight: 48 // Match the fixed height from the main component
          }}>
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
          <Skeleton variant="text" width={80} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={100} height={16} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderLeftColor: getVariantColor(),
        boxShadow: theme.shadows[2],
        position: 'relative', // Add relative positioning for absolute child
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        }
      }}
    >
      {/* Help button in absolute top-right corner */}
      {description && (
        <Tooltip 
          title={description} 
          arrow 
          placement="top"
          enterDelay={300}
          leaveDelay={200}
          sx={{
            '& .MuiTooltip-tooltip': {
              maxWidth: 300,
              fontSize: '0.875rem',
              backgroundColor: theme.palette.grey[800],
              color: theme.palette.common.white,
              padding: theme.spacing(1.5),
              borderRadius: theme.spacing(1)
            }
          }}
        >
          <IconButton 
            size="small" 
            sx={{ 
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: theme.palette.text.secondary,
              '&:hover': {
                color: getVariantColor(),
                backgroundColor: theme.palette.action.hover
              },
              p: 0.5
            }}
          >
            <HelpIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      
      <CardContent sx={{ p: 3 }}>
        {/* Header with title and icon */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between', 
          mb: 2,
          minHeight: 48 // Fixed height to ensure consistent spacing
        }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ 
              fontWeight: 500, 
              textTransform: 'uppercase', 
              letterSpacing: 0.5,
              lineHeight: 1.2,
              maxWidth: '80%' // Prevent title from overlapping icon
            }}
          >
            {title}
          </Typography>
          {icon && (
            <Box sx={{ 
              color: getVariantColor(), 
              display: 'flex', 
              alignItems: 'center',
              mt: 0.5 // Small top margin to align with first line of text
            }}>
              {icon}
            </Box>
          )}
        </Box>

        {/* Main value */}
        <Typography
          variant="h3"
          component="div"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 1,
            lineHeight: 1.1
          }}
        >
          {formatValue(value)}
        </Typography>

        {/* Change indicator and subtitle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {change !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getTrendIcon()}
              <Typography
                variant="body2"
                sx={{
                  color: trend === 'up' ? theme.palette.success.main :
                         trend === 'down' ? theme.palette.error.main :
                         theme.palette.text.secondary,
                  fontWeight: 500
                }}
              >
                {change > 0 ? '+' : ''}{change}%
              </Typography>
            </Box>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
